import { useState, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, QrCode, Search, Vote, Calendar as CalendarIcon, Bell, Waves, FileText, Wrench,
  CheckSquare, ShoppingCart, PawPrint, Car, Wallet, Users, UserPlus, MapPin, CalendarCheck, MessageSquare,
  UserCircle, LogOut, Menu, X, Home, ArrowRight, TrendingUp, Shield, CheckCircle, Clock, AlertCircle,
  DollarSign, FileCheck, Download, Eye, MessageCircle, Phone, BookOpen, Coins, Building, ShieldCheck, Users2, 
  ClipboardList, Building2, Key, BarChart3, Tags, CreditCard, Settings, AlertTriangle
} from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import { NotificationBell, NotificationsDropdown } from './components/Notifications';
import api from './services/api';
import OnboardingChecklist from './components/OnboardingChecklist';

// Helper to filter modules by role
const getModuleGroups = (userRole: string) => {
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const isDirective = userRole === 'directive' || userRole === 'super_admin';
  const isResident = userRole === 'resident' || userRole === 'directive';
  const isSecurity = userRole === 'security';

  // Security guard limited modules
  if (isSecurity) {
    return [
      {
        title: 'Condominio',
        modules: [
          { id: 'verify', icon: Search, label: 'Verificar', desc: 'Validar visitantes' },
          { id: 'amenities', icon: Waves, label: 'Amenidades', desc: 'Reservar ahora' },
          { id: 'announcements', icon: Bell, label: 'Anuncios', desc: 'Nuevos: 2' },
        ]
      },
      {
        title: 'Comunidad',
        modules: [
          { id: 'emergencias', icon: Phone, label: 'Emergencias', desc: 'Números útiles' },
          { id: 'reporte-visitas', icon: ClipboardList, label: 'Visitas', desc: 'Historial' },
          { id: 'llave-digital', icon: Key, label: 'Llave Digital', desc: 'Acceso 24/7' },
        ]
      },
      {
        title: 'Configuración',
        modules: [
          { id: 'perfil-completo', icon: UserCircle, label: 'Mi Perfil', desc: 'Editar cuenta' },
        ]
      }
    ];
  }

  return [
    {
      title: 'Accesos y Seguridad',
      modules: [
        { id: 'qr', icon: QrCode, label: 'Mi Acceso', desc: 'Tu código QR personal' },
        ...(isAdmin ? [{ id: 'verify', icon: Search, label: 'Verificar', desc: 'Validar visitantes' }] : []),
        { id: 'panic', icon: AlertTriangle, label: 'Emergencia', desc: 'Alertas y números' },
      ].filter(Boolean)
    },
    {
      title: 'Finanzas',
      modules: [
        ...(isAdmin || isDirective ? [{ id: 'financial', icon: FileText, label: 'Reportes', desc: 'Ver reportes' }] : []),
        { id: 'expenses', icon: DollarSign, label: 'Gastos', desc: 'Transparencia' },
        ...(isAdmin || isDirective ? [{ id: 'morosos', icon: AlertCircle, label: 'Morosos', desc: 'Pendientes' }] : []),
      ].filter(Boolean)
    },
    {
      title: 'Comunidad',
      modules: [
        { id: 'voting', icon: Vote, label: 'Votaciones', desc: 'Activas: 2' },
        { id: 'calendar', icon: CalendarIcon, label: 'Calendario', desc: 'Hoy: 3 eventos' },
        { id: 'announcements', icon: Bell, label: 'Anuncios', desc: 'Nuevos: 2' },
      ].filter(Boolean)
    },
    {
      title: 'Instalaciones',
      modules: [
        { id: 'amenities', icon: Waves, label: 'Amenidades', desc: 'Reservar ahora' },
        ...(isAdmin ? [{ id: 'work-orders', icon: Wrench, label: 'Órdenes', desc: 'Pendientes: 3' }] : []),
      ].filter(Boolean)
    },
    {
      title: 'Comunidad',
      modules: [
        { id: 'quejas', icon: MessageCircle, label: 'Quejas', desc: 'Tickets activos' },
        { id: 'emergencias', icon: Phone, label: 'Emergencias', desc: 'Números útiles' },
        { id: 'reglamentos', icon: BookOpen, label: 'Reglamentos', desc: 'Documentos' },
        ...(isAdmin ? [{ id: 'caja-chica', icon: Coins, label: 'Caja Chica', desc: 'Fondo fijo' }] : []),
        ...(isAdmin ? [{ id: 'casetas', icon: Building, label: 'Casetas', desc: 'Vigilancia' }] : []),
        ...(isAdmin ? [{ id: 'empresas-seguridad', icon: ShieldCheck, label: 'Emp. Seguridad', desc: 'Proveedores' }] : []),
        ...(isAdmin ? [{ id: 'personal-seguridad', icon: Users2, label: 'Personal', desc: 'Guardias' }] : []),
        ...(isAdmin ? [{ id: 'reporte-visitas', icon: ClipboardList, label: 'Visitas', desc: 'Historial' }] : []),
        { id: 'info-condominio', icon: Building2, label: 'Info Condominio', desc: 'Datos' },
        { id: 'llave-digital', icon: Key, label: 'Llave Digital', desc: 'Acceso 24/7' },
        { id: 'votaciones-avanzadas', icon: BarChart3, label: 'Votaciones 2.0', desc: 'Encuestas' },
        ...(isAdmin ? [{ id: 'categorias', icon: Tags, label: 'Categorías', desc: 'Ingresos/Egresos' }] : []),
        { id: 'reserva-pago', icon: CreditCard, label: 'Reserva + Pago', desc: 'Con Openpay' },
      ].filter(Boolean)
    },
    ...(isResident || isSuperAdmin ? [{
      title: 'Hogar',
      modules: [
        { id: 'tasks', icon: CheckSquare, label: 'Tareas', desc: 'Pendientes: 5' },
        { id: 'shopping', icon: ShoppingCart, label: 'Compras', desc: 'Lista activa' },
        { id: 'pets', icon: PawPrint, label: 'Mascotas', desc: 'Registros: 2' },
        { id: 'vehicles', icon: Car, label: 'Vehículos', desc: 'Mantenimiento: 1' },
        { id: 'budget', icon: Wallet, label: 'Presupuesto', desc: 'On track' },
      ]
    }] : []),
    {
      title: 'Proveedores',
      modules: [
        { id: 'suppliers', icon: Users, label: 'Directorio', desc: '45 proveedores' },
        { id: 'become-supplier', icon: UserPlus, label: 'Ser Proveedor', desc: 'Regístrate' },
        ...(isSuperAdmin ? [{ id: 'route', icon: MapPin, label: 'Rutas', desc: 'Optimizar' }] : []),
      ].filter(Boolean)
    },
    ...(isAdmin ? [{
      title: 'Rentas',
      modules: [
        { id: 'reservations', icon: CalendarCheck, label: 'Reservas', desc: 'Esta semana: 8' },
        { id: 'messages', icon: MessageSquare, label: 'Mensajes', desc: 'Nuevos: 5' },
      ]
    }] : []),
    {
      title: 'Configuración',
      modules: [
        { id: 'perfil-completo', icon: UserCircle, label: 'Mi Perfil', desc: 'Editar cuenta' },
        ...(isSuperAdmin ? [{ id: 'openpay-config', icon: Settings, label: 'Openpay', desc: 'Pagos' }] : []),
      ].filter(Boolean)
    }
  ];
};

const pendingApprovals = [
  { id: '1', type: 'expense', title: 'Reparación Elevador', amount: '$15,000', date: '2026-03-18', status: 'pending', requestor: 'Admin Torres', icon: Wrench },
  { id: '2', type: 'project', title: 'Pintura Fachada', amount: '$45,000', date: '2026-03-15', status: 'pending', requestor: 'Admin García', icon: FileText },
  { id: '3', type: 'expense', title: 'Mantenimiento Jardines', amount: '$3,500', date: '2026-03-10', status: 'approved', requestor: 'Admin López', icon: Waves },
];

const morosos = [
  { id: '1', unit: 'A-101', name: 'Carlos Méndez', debt: '$8,500', months: 3, lastPayment: 'Dic 2025' },
  { id: '2', unit: 'B-205', name: 'María Santos', debt: '$12,000', months: 4, lastPayment: 'Nov 2025' },
  { id: '3', unit: 'C-302', name: 'Pedro Vargas', debt: '$5,200', months: 2, lastPayment: 'Ene 2026' },
];

export default function Dashboard({ children }: { children?: ReactNode }) {
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin, isAdmin, isDirective } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showNotifications, setShowNotifications] = useState(false);
  const [approvals, setApprovals] = useState(pendingApprovals);
  const [residentAccount, setResidentAccount] = useState<any>(null);
  const [residentAccountState, setResidentAccountState] = useState<'loading' | 'ready' | 'empty'>('loading');

  useEffect(() => {
    const residentRoles = ['resident', 'propietario', 'inquilino', 'familiar'];
    if (!residentRoles.includes(user?.role || '') || !user?.condoId || !user?.id) return;
    let active = true;
    api.get(`/financial/condo/${user.condoId}/account/${user.id}`)
      .then((response) => {
        if (!active) return;
        const account = response.data?.data ?? response.data ?? {};
        setResidentAccount(account);
        setResidentAccountState(account && (account.balance !== undefined || account.total_due !== undefined || account.charges?.length || account.payments?.length) ? 'ready' : 'empty');
      })
      .catch(() => { if (active) setResidentAccountState('empty'); })
      .finally(() => { if (active && residentAccountState === 'loading') setResidentAccountState((current) => current === 'loading' ? 'empty' : current); });
    return () => { active = false; };
  }, [user?.role, user?.condoId, user?.id]);
  
  // Handle approve action
  const handleApprove = (id: string) => {
    setApprovals(prev => prev.map(item => 
      item.id === id ? { ...item, status: 'approved' } : item
    ));
    // Show success toast (would integrate with toast system)
    alert(`Gasto aprobado exitosamente`);
  };
  
  // Handle view details action
  const handleViewDetails = (id: string) => {
    navigate(`/expense-details/${id}`);
  };
  
  // Get role-based module groups
  const moduleGroups = getModuleGroups(user?.role || 'resident');
  
  // Show directive panel only for directive and super_admin
  const showDirectivePanel = user?.role === 'directive' || user?.role === 'super_admin';
  
  // Show admin panel only for super_admin
  const showAdminPanel = user?.role === 'super_admin';

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Unified dashboard data
  const dashboardData = {
    tasks: 3,
    messages: 2,
    notifications: 3,
    reservations: 2,
    codes: 12,
    visitors: 8,
    orders: 3,
    payments: '$45K',
    pendingApproval: 2,
    morososCount: 3,
    activeVotings: 2,
  };

  // Get role-based stats with proper color mapping
  const getRoleBasedStats = () => {
    const role = user?.role;
    const d = dashboardData;
    
    switch (role) {
      case 'security':
        return [
          { label: 'Visitantes Activos', value: '2', icon: Users, color: '#36CCD8' },
          { label: 'Alertas Críticas', value: '1', icon: AlertTriangle, color: '#EF4444' },
          { label: 'Códigos Válidos', value: d.codes.toString(), icon: QrCode, color: '#10B981' },
          { label: 'Turno', value: 'Diurno', icon: Clock, color: '#F59E0B' },
        ];
      case 'resident':
        return [
          { label: 'Estado de cuenta', value: residentAccountState === 'ready' ? `$${Number(residentAccount?.balance ?? residentAccount?.total_due ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : 'Sin datos', icon: Wallet, color: '#36CCD8' },
          { label: 'Reservas', value: `${d.reservations} Activas`, icon: Waves, color: '#10B981' },
          { label: 'Tareas', value: `${d.tasks} Pendientes`, icon: CheckSquare, color: '#F59E0B' },
          { label: 'Notificaciones', value: `${d.notifications} Nuevas`, icon: Bell, color: '#8B5CF6' },
        ];
      case 'directive':
        return [
          { label: 'Por Aprobar', value: d.pendingApproval.toString(), icon: Shield, color: '#36CCD8' },
          { label: 'Morosos', value: d.morososCount.toString(), icon: AlertCircle, color: '#EF4444' },
          { label: 'Votaciones', value: `${d.activeVotings} Activas`, icon: Vote, color: '#10B981' },
          { label: 'Visitantes', value: `${d.visitors} Hoy`, icon: Users, color: '#F59E0B' },
        ];
      case 'admin':
      case 'super_admin':
        return [
          { label: 'Códigos Activos', value: d.codes.toString(), icon: QrCode, color: '#36CCD8' },
          { label: 'Visitantes Hoy', value: d.visitors.toString(), icon: Users, color: '#10B981' },
          { label: 'Órdenes', value: d.orders.toString(), icon: Wrench, color: '#F59E0B' },
          { label: 'Pagos', value: d.payments, icon: TrendingUp, color: '#8B5CF6' },
        ];
      default:
        return [
          { label: 'Códigos', value: d.codes.toString(), icon: QrCode, color: '#36CCD8' },
          { label: 'Visitantes', value: d.visitors.toString(), icon: Users, color: '#10B981' },
        ];
    }
  };

  const stats = getRoleBasedStats();

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2 text-[#2D3748] hover:bg-gray-100 rounded-xl">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#36CCD8] rounded-xl flex items-center justify-center">
                <div className="flex gap-0.5">
                  <Home className="w-4 h-4 text-white" />
                  <Home className="w-4 h-4 text-white -ml-1.5" />
                </div>
              </div>
              <div className="hidden sm:block">
                <span className="text-lg font-bold text-[#2D3748]">Vecindus</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {showDirectivePanel && (
              <button onClick={() => navigate('/directive')} className="hidden md:flex items-center gap-2 px-4 py-2 bg-[#36CCD8]/10 text-[#36CCD8] dark:bg-[#36CCD8]/15 dark:text-[#4DD6E0] rounded-xl font-medium">
                <Shield className="w-4 h-4" /> Panel Directivo
              </button>
            )}
            
            <div className="hidden md:block text-right mr-2">
              <p className="text-sm font-bold text-[#2D3748]">{user?.name || 'Usuario'}</p>
              <p className="text-xs text-gray-500">{currentTime.toLocaleDateString('es-MX', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
            </div>
            
            <div className="relative">
              <NotificationBell onClick={() => setShowNotifications(!showNotifications)} unreadCount={2} />
              {showNotifications && <NotificationsDropdown onClose={() => setShowNotifications(false)} />}
            </div>
            
            <button onClick={() => navigate('/profile')} className="p-2 text-gray-600 hover:text-[#36CCD8] hover:bg-[#36CCD8]/10 dark:text-slate-300 dark:hover:text-[#4DD6E0] rounded-xl">
              <UserCircle className="w-5 h-5" />
            </button>
            
            <button onClick={logout} className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-xl">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex pt-16">
        <aside className={`fixed lg:sticky top-16 left-0 w-72 h-[calc(100vh-64px)] bg-white border-r border-gray-200 overflow-y-auto z-40 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
          <nav className="p-4">
            <button onClick={() => { setActiveTab('overview'); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold mb-2 transition-all ${activeTab === 'overview' ? 'bg-[#36CCD8] text-white shadow-lg shadow-[#36CCD8]/30' : 'text-[#2D3748] hover:bg-gray-100'}`}>
              <Home className="w-5 h-5" /> Dashboard
            </button>
            
            {moduleGroups.map((group) => (
              <div key={group.title} className="mt-6">
                <h3 className="px-4 text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{group.title}</h3>
                {group.modules.map((module) => (
                  <button
                    key={module.id}
                    onClick={() => { 
                      setActiveTab(module.id); 
                      setSidebarOpen(false);
                      // Navigate to Mi ResidenciApp modules
                      const miResidenciRoutes: Record<string, string> = {
                        'quejas': '/quejas',
                        'emergencias': '/emergencias',
                        'reglamentos': '/reglamentos',
                        'caja-chica': '/caja-chica',
                        'casetas': '/casetas',
                        'empresas-seguridad': '/empresas-seguridad',
                        'personal-seguridad': '/personal-seguridad',
                        'reporte-visitas': '/reporte-visitas',
                        'info-condominio': '/info-condominio',
                        'llave-digital': '/llave-digital',
                        'votaciones-avanzadas': '/votaciones-avanzadas',
                        'categorias': '/categorias',
                        'reserva-pago': '/reserva-pago',
                        // Existing routes
                        'qr': '/qr',
                        'verify': '/verify',
                        'voting': '/voting',
                        'calendar': '/calendar',
                        'announcements': '/announcements',
                        'amenities': '/amenities',
                        'financial': '/financial-reports',
                        'morosos': '/morosos',
                        'expenses': '/expenses',
                        'panic': '/emergencias',
                        'work-orders': '/work-orders',
                        'tasks': '/tasks',
                        'shopping': '/shopping',
                        'pets': '/pets',
                        'vehicles': '/vehicles',
                        'budget': '/budget',
                        'suppliers': '/suppliers',
                        'become-supplier': '/become-supplier',
                        'route': '/route-optimizer',
                        'reservations': '/reservations',
                        'messages': '/guest-comm',
                        'perfil-completo': '/perfil-completo',
                        'openpay-config': '/openpay-config',
                      };
                      const route = miResidenciRoutes[module.id];
                      if (route) navigate(route);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all mb-1 ${activeTab === module.id ? 'bg-[#36CCD8]/10 text-[#36CCD8] border border-[#36CCD8]/30' : 'text-[#2D3748] hover:bg-gray-100'}`}
                  >
                    <module.icon className="w-4 h-4" /> 
                    <span>{module.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </nav>
        </aside>

        {sidebarOpen && <div className="fixed inset-0 bg-black/20 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

        <main className="flex-1 p-6 min-h-[calc(100vh-64px)]">
          {children ? children : <>
          <OnboardingChecklist />
          <div className="bg-gradient-to-r from-[#36CCD8] to-[#2DB8C4] rounded-2xl p-6 text-white mb-4 shadow-lg shadow-[#36CCD8]/20">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <p className="text-white/80 text-sm font-medium mb-1">Bienvenido de vuelta</p>
                <h1 className="text-2xl font-bold">¡Hola, {user?.name?.split(' ')[0] || 'Usuario'}!</h1>
                <p className="text-white/90 mt-1">
                  {user?.role === 'resident' ? (
                    <>
                      Tienes <strong>{dashboardData.tasks} tareas</strong> pendientes y <strong>{dashboardData.messages} mensajes</strong> nuevos.
                    </>
                  ) : user?.role === 'directive' ? (
                    <>
                      <strong>{dashboardData.pendingApproval} gastos</strong> por aprobar y <strong>{dashboardData.morososCount} morosos</strong> este mes.
                    </>
                  ) : (
                    <>
                      <strong>{dashboardData.visitors} visitantes</strong> hoy y <strong>{dashboardData.orders} órdenes</strong> pendientes.
                    </>
                  )}
                </p>
              </div>
              
              <div className="flex gap-3">
                {showAdminPanel && (
                  <button onClick={() => navigate('/admin')} className="px-4 py-2 bg-white text-[#36CCD8] rounded-xl text-sm font-bold hover:bg-gray-50 dark:bg-[var(--v-surface-2)] dark:text-[#4DD6E0] dark:hover:bg-[var(--v-surface-3)] shadow-md transition-all">
                    Panel Admin
                  </button>
                )}
                {showDirectivePanel && (
                  <button onClick={() => navigate('/directive')} className="px-4 py-2 bg-white/20 text-white rounded-xl text-sm font-bold hover:bg-white/30 dark:bg-white/10 dark:hover:bg-white/20 transition-all">
                    Panel Directivo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {stats.map((stat, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${stat.color}15` }}>
                  <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
                </div>
                <p className="text-2xl font-bold text-[#2D3748]">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>

          {showDirectivePanel && (
            <div className="grid lg:grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 bg-gray-50/50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-[#36CCD8]/10 rounded-xl flex items-center justify-center">
                      <Shield className="w-5 h-5 text-[#36CCD8]" />
                    </div>
                    <h3 className="font-bold text-[#2D3748]">Pendientes de Aprobación</h3>
                  </div>
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">{dashboardData.pendingApproval} por revisar</span>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {approvals.filter(p => p.status === 'pending').map((item) => (
                    <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-[#36CCD8]/10 rounded-xl flex items-center justify-center">
                          <item.icon className="w-5 h-5 text-[#36CCD8]" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-[#2D3748]">{item.title}</p>
                          <p className="text-sm text-gray-500">Solicitado por {item.requestor}</p>
                          <p className="text-lg font-bold text-[#36CCD8] mt-1">{item.amount}</p>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => handleApprove(item.id)}
                            className="w-10 h-10 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 flex items-center justify-center transition-colors"
                            title="Aprobar"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                          <button 
                            onClick={() => handleViewDetails(item.id)}
                            className="w-10 h-10 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 flex items-center justify-center transition-colors"
                            title="Ver detalles"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 bg-gray-50/50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                    <h3 className="font-bold text-[#2D3748] dark:text-slate-200">Morosos</h3>
                  </div>
                  <button onClick={() => navigate('/morosos')} className="text-sm font-bold text-[#36CCD8] hover:text-[#2DB8C4] dark:text-[#4DD6E0] dark:hover:text-[#67DFE7]">Ver todos</button>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {morosos.map((m) => (
                    <div key={m.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-[#2D3748]">{m.name}</p>
                          <p className="text-sm text-gray-500">Unidad {m.unit} • {m.months} meses</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-red-500">{m.debt}</p>
                          <p className="text-xs text-gray-400">Último: {m.lastPayment}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {showDirectivePanel && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center">
              <div className="w-16 h-16 bg-[#36CCD8]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <LayoutDashboard className="w-8 h-8 text-[#36CCD8]" />
              </div>
              <h2 className="text-xl font-bold text-[#2D3748] dark:text-slate-200 mb-2">Dashboard Vecindus</h2>
              <p className="text-gray-500 dark:text-slate-400 mb-4">Selecciona un módulo del menú lateral para comenzar</p>
              <button onClick={() => navigate('/directive')} className="px-6 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] transition-all">
                Ir a Panel Directivo
              </button>
            </div>
          )}
          </>}
        </main>
      </div>
    </div>
  );
}
